import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, DataTable, Section, Loading, KPITile } from "../components/Common";
import { useAuth } from "../lib/auth";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function StudentPortal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  useEffect(() => {
    api.studentOverview().then(({ data }) => setData(data));
    api.subjects().then(({ data }) => setSubjects(data));
  }, []);
  const subjMap = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
  if (!data) return <Loading />;

  const gradesBySubject = (data.grades || []).reduce((acc, g) => {
    const s = subjMap[g.subject_id];
    if (!s) return acc;
    (acc[s.name] = acc[s.name] || []).push(g);
    return acc;
  }, {});

  const gridEntries = (data.timetable || []).reduce((a, e) => {
    a[e.day] = a[e.day] || {};
    a[e.day][e.period] = e;
    return a;
  }, {});

  return (
    <div data-testid="student-portal">
      <PageHeader kicker="Student Portal" title={`Hi, ${user?.full_name}`} subtitle={data.student ? `${data.student.year_group} · ${data.student.house} House` : ""} />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <KPITile tone="dark" testId="sp-year" label="Year Group" value={data.student?.year_group || "—"} />
        <KPITile testId="sp-house" label="House" value={data.student?.house || "—"} />
        <KPITile testId="sp-subjects" label="Subjects" value={Object.keys(gradesBySubject).length} />
      </div>

      <Section title="My timetable">
        <div className="hl-card overflow-x-auto">
          <table className="hl-table">
            <thead>
              <tr>
                <th>Period</th>
                {DAYS.map((d) => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((p) => (
                <tr key={p}>
                  <td className="font-semibold">P{p}</td>
                  {DAYS.map((d) => {
                    const e = gridEntries[d]?.[p];
                    return (
                      <td key={d}>
                        {e ? (
                          <>
                            <div className="text-sm font-semibold">{subjMap[e.subject_id]?.name}</div>
                            <div className="text-xs text-slate-500">{e.start_time}-{e.end_time}</div>
                          </>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="My grades">
        <DataTable
          columns={[
            { key: "subject", label: "Subject" },
            { key: "assessments", label: "Assessments" },
            { key: "average", label: "Average" },
          ]}
          rows={Object.entries(gradesBySubject).map(([name, list]) => {
            const avg = list.reduce((a, g) => a + (g.score / g.max_score) * 100, 0) / list.length;
            return { id: name, subject: name, assessments: list.length, average: `${avg.toFixed(1)}%` };
          })}
        />
      </Section>

      <Section title="School notices">
        <div className="space-y-3">
          {(data.notifications || []).map((n) => (
            <div key={n.id} className="hl-card p-5">
              <div className="font-display font-bold text-base tracking-tight">{n.title}</div>
              <div className="text-sm text-slate-600 mt-1">{n.body}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
